const DEFAULT_OPTIONS = {
  viewportSelector: ".js-horizontalScroll__viewport",
  scrollbarSelector: ".js-horizontalScroll__scrollbar",
  thumbSelector: ".js-horizontalScroll__thumb",
  initializedClass: "is-initialized",
  scrollableClass: "is-scrollable",
  draggingClass: "is-dragging",
  minThumbWidth: 40,
  smoothTrackClick: true,
};

/**
 * 1つの横スクロール領域を管理する内部クラス。
 */
class HorizontalScrollItem {
  /**
   * @param {HTMLElement} root
   * @param {HorizontalScrollOptions} options
   */
  constructor(root, options) {
    this.root = root;
    this.options = options;

    this.viewport = root.querySelector(options.viewportSelector);
    this.scrollbar = root.querySelector(options.scrollbarSelector);
    this.thumb = root.querySelector(options.thumbSelector);

    this.isInitialized = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.resizeObserver = null;
    this.pendingImages = [];

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleThumbPointerDown = this.handleThumbPointerDown.bind(this);
    this.handleThumbPointerMove = this.handleThumbPointerMove.bind(this);
    this.handleThumbPointerEnd = this.handleThumbPointerEnd.bind(this);
    this.handleTrackPointerDown = this.handleTrackPointerDown.bind(this);

    this.init();
  }

  init() {
    if (!this.viewport || !this.scrollbar || !this.thumb) {
      console.warn(
        "HorizontalScroll: 必要な子要素が見つかりません。",
        this.root
      );
      return;
    }

    this.viewport.addEventListener("scroll", this.handleScroll, {
      passive: true,
    });

    this.thumb.addEventListener(
      "pointerdown",
      this.handleThumbPointerDown
    );
    this.thumb.addEventListener(
      "pointermove",
      this.handleThumbPointerMove
    );
    this.thumb.addEventListener(
      "pointerup",
      this.handleThumbPointerEnd
    );
    this.thumb.addEventListener(
      "pointercancel",
      this.handleThumbPointerEnd
    );

    this.scrollbar.addEventListener(
      "pointerdown",
      this.handleTrackPointerDown
    );

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.root);
      this.resizeObserver.observe(this.viewport);

      const content = this.viewport.firstElementChild;
      if (content) {
        this.resizeObserver.observe(content);
      }
    } else {
      window.addEventListener("resize", this.handleResize);
    }

    this.observeImages();

    this.root.dataset.horizontalScrollInitialized = "true";
    this.root.classList.add(this.options.initializedClass);

    this.isInitialized = true;
    this.update();
  }

  /**
   * viewport内の画像は読み込み完了後にサイズが変化するが、
   * その変化はコンテナ自体のボックスサイズを変えないため
   * ResizeObserverでは検知できない。load/errorを直接監視して補う。
   */
  observeImages() {
    const images = this.viewport.querySelectorAll("img");

    images.forEach((image) => {
      if (image.complete) return;

      this.pendingImages.push(image);
      image.addEventListener("load", this.handleResize);
      image.addEventListener("error", this.handleResize);
    });
  }

  update() {
    if (!this.isInitialized) return;

    const viewportWidth = this.viewport.clientWidth;
    const scrollWidth = this.viewport.scrollWidth;
    const isScrollable = scrollWidth > viewportWidth + 1;

    this.root.classList.toggle(
      this.options.scrollableClass,
      isScrollable
    );
    this.scrollbar.hidden = !isScrollable;

    if (!isScrollable) {
      this.viewport.scrollLeft = 0;
      this.thumb.style.width = "";
      this.thumb.style.transform = "";
      return;
    }

    const scrollbarWidth = this.scrollbar.clientWidth;
    const calculatedWidth =
      (viewportWidth / scrollWidth) * scrollbarWidth;

    const thumbWidth = Math.min(
      scrollbarWidth,
      Math.max(calculatedWidth, this.options.minThumbWidth)
    );

    this.thumb.style.width = `${thumbWidth}px`;
    this.updateThumbPosition();
  }

  updateThumbPosition() {
    if (!this.isInitialized) return;

    const maxScrollLeft =
      this.viewport.scrollWidth - this.viewport.clientWidth;
    const maxThumbLeft =
      this.scrollbar.clientWidth - this.thumb.offsetWidth;

    const thumbLeft =
      maxScrollLeft > 0
        ? (this.viewport.scrollLeft / maxScrollLeft) * maxThumbLeft
        : 0;

    this.thumb.style.transform = `translate3d(${thumbLeft}px, 0, 0)`;
  }

  handleScroll() {
    this.updateThumbPosition();
  }

  handleResize() {
    this.update();
  }

  /**
   * @param {PointerEvent} event
   */
  handleThumbPointerDown(event) {
    if (event.button !== 0) return;

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.viewport.scrollLeft;

    this.thumb.classList.add(this.options.draggingClass);
    this.thumb.setPointerCapture(event.pointerId);

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * @param {PointerEvent} event
   */
  handleThumbPointerMove(event) {
    if (!this.isDragging) return;

    const maxThumbLeft =
      this.scrollbar.clientWidth - this.thumb.offsetWidth;
    const maxScrollLeft =
      this.viewport.scrollWidth - this.viewport.clientWidth;

    if (maxThumbLeft <= 0 || maxScrollLeft <= 0) return;

    const dragDistance = event.clientX - this.dragStartX;
    const scrollDistance =
      (dragDistance / maxThumbLeft) * maxScrollLeft;

    this.viewport.scrollLeft =
      this.dragStartScrollLeft + scrollDistance;
  }

  /**
   * @param {PointerEvent} event
   */
  handleThumbPointerEnd(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.thumb.classList.remove(this.options.draggingClass);

    if (this.thumb.hasPointerCapture(event.pointerId)) {
      this.thumb.releasePointerCapture(event.pointerId);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  handleTrackPointerDown(event) {
    if (event.target === this.thumb || event.button !== 0) return;

    const trackRect = this.scrollbar.getBoundingClientRect();
    const thumbWidth = this.thumb.offsetWidth;
    const maxThumbLeft =
      this.scrollbar.clientWidth - thumbWidth;
    const maxScrollLeft =
      this.viewport.scrollWidth - this.viewport.clientWidth;

    const requestedThumbLeft =
      event.clientX - trackRect.left - thumbWidth / 2;

    const thumbLeft = Math.max(
      0,
      Math.min(requestedThumbLeft, maxThumbLeft)
    );

    const scrollLeft =
      maxThumbLeft > 0
        ? (thumbLeft / maxThumbLeft) * maxScrollLeft
        : 0;

    if (this.options.smoothTrackClick) {
      this.viewport.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
      return;
    }

    this.viewport.scrollLeft = scrollLeft;
  }

  destroy() {
    if (!this.isInitialized) return;

    this.viewport.removeEventListener("scroll", this.handleScroll);
    this.thumb.removeEventListener(
      "pointerdown",
      this.handleThumbPointerDown
    );
    this.thumb.removeEventListener(
      "pointermove",
      this.handleThumbPointerMove
    );
    this.thumb.removeEventListener(
      "pointerup",
      this.handleThumbPointerEnd
    );
    this.thumb.removeEventListener(
      "pointercancel",
      this.handleThumbPointerEnd
    );
    this.scrollbar.removeEventListener(
      "pointerdown",
      this.handleTrackPointerDown
    );

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener("resize", this.handleResize);
    }

    this.pendingImages.forEach((image) => {
      image.removeEventListener("load", this.handleResize);
      image.removeEventListener("error", this.handleResize);
    });
    this.pendingImages = [];

    this.scrollbar.hidden = false;
    this.thumb.style.width = "";
    this.thumb.style.transform = "";
    this.thumb.classList.remove(this.options.draggingClass);

    this.root.classList.remove(
      this.options.initializedClass,
      this.options.scrollableClass
    );

    delete this.root.dataset.horizontalScrollInitialized;

    this.isInitialized = false;
  }
}

/**
 * 任意の横長コンテンツにカスタム横スクロールバーを追加する。
 */
export default class HorizontalScroll {
  /**
   * @param {string|HTMLElement|NodeListOf<HTMLElement>|HTMLElement[]} target
   * @param {Partial<HorizontalScrollOptions>} options
   */
  constructor(target = ".js-horizontalScroll", options = {}) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    this.elements = this.getElements(target);
    this.instances = [];

    this.init();
  }

  /**
   * @param {string|HTMLElement|NodeListOf<HTMLElement>|HTMLElement[]} target
   * @returns {HTMLElement[]}
   */
  getElements(target) {
    if (typeof target === "string") {
      return [...document.querySelectorAll(target)];
    }

    if (target instanceof HTMLElement) {
      return [target];
    }

    if (target instanceof NodeList || Array.isArray(target)) {
      return [...target].filter(
        (element) => element instanceof HTMLElement
      );
    }

    return [];
  }

  init() {
    this.elements.forEach((element) => {
      if (
        element.dataset.horizontalScrollInitialized === "true"
      ) {
        return;
      }

      const instance = new HorizontalScrollItem(
        element,
        this.options
      );

      if (instance.isInitialized) {
        this.instances.push(instance);
      }
    });

    return this;
  }

  update() {
    this.instances.forEach((instance) => instance.update());
    return this;
  }

  destroy() {
    this.instances.forEach((instance) => instance.destroy());
    this.instances = [];
    return this;
  }
}

/**
 * @typedef {Object} HorizontalScrollOptions
 * @property {string} viewportSelector
 * @property {string} scrollbarSelector
 * @property {string} thumbSelector
 * @property {string} initializedClass
 * @property {string} scrollableClass
 * @property {string} draggingClass
 * @property {number} minThumbWidth
 * @property {boolean} smoothTrackClick
 */
